/**-----------------------------------------------------------------------------------------
* Copyright © 2023 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import * as i0 from '@angular/core';
import { Directive, Input, HostBinding, NgModule } from '@angular/core';
import { register } from '@progress/kendo-ripple';
import { isDocumentAvailable } from '@progress/kendo-angular-common';
import { validatePackage } from '@progress/kendo-licensing';

/**
 * @hidden
 */
const packageMetadata = {
    name: '@progress/kendo-angular-ripple',
    productName: 'Kendo UI for Angular',
    productCodes: ['KENDOUIANGULAR', 'KENDOUICOMPLETE'],
    publishDate: 1684310480,
    version: '12.1.0',
    licensingDocsUrl: 'https://www.telerik.com/kendo-angular-ui/my-license/'
};

/**
 * Represents the Ripple container component.
 *
 * You can apply this directive to any container element.
 * The ripple effect will show on the following elements:
 * - Buttons
 * - Checkboxes
 * - Radio buttons
 *
 * @example
 * ```ts-no-run
 * <div kendoRippleContainer>
 *     <button kendoButton>Default Button</button>
 *
 *     <button kendoButton [primary]="true">Primary Button</button>
 * </div>
 * ```
 */
class RippleContainerDirective {
    constructor(renderer, element, ngZone) {
        this.renderer = renderer;
        this.element = element;
        this.ngZone = ngZone;
        this.isDisabled = false;
        this.removeListeners = () => { };
        validatePackage(packageMetadata);
    }
    /**
     * Provides an option to disable the ripple effect of the `kendoRippleContainer` element.
     * By default, `disabled` is set to `false`.
     */
    set disabled(disabled) {
        this.isDisabled = disabled;
        if (this.isDisabled) {
            this.removeListeners();
        }
        else {
            this.registerListeners();
        }
        this.renderer.setProperty(this.element.nativeElement, 'disabled', disabled);
    }
    get containerClass() {
        return true;
    }
    ngOnDestroy() {
        this.removeListeners();
    }
    ngAfterViewInit() {
        if (!this.isDisabled) {
            this.ngZone.runOutsideAngular(() => {
                this.registerListeners();
            });
        }
    }
    registerListeners() {
        if (!isDocumentAvailable()) {
            return;
        }
        this.removeListeners();
        const callback = register(this.element.nativeElement, [
            { selector: ".k-button:not(li)" },
            { selector: ".k-list>.k-item", options: { global: true } },
            { selector: ".k-checkbox,.k-radio",
                options: {
                    events: ["focusin", "animationend", "click"]
                }
            }
        ]);
        this.removeListeners = callback;
    }
}
RippleContainerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RippleContainerDirective, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive });
RippleContainerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: RippleContainerDirective, selector: "[kendoRippleContainer]", inputs: { disabled: "disabled" }, host: { properties: { "class.k-ripple-container": "this.containerClass" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RippleContainerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kendoRippleContainer]'
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.NgZone }]; }, propDecorators: { disabled: [{
                type: Input
            }], containerClass: [{
                type: HostBinding,
                args: ['class.k-ripple-container']
            }] } });

const COMPONENT_DIRECTIVES = [RippleContainerDirective];
/**
 * Represents the [NgModule](link:site.data.urls.angular['ngmoduleapi'])
 * definition for the Ripple directive.
 *
 * @example
 *
 * ```ts-no-run
 * // Import the Ripple module
 * import { RippleModule } from '@progress/kendo-angular-ripple';
 *
 * // The browser platform with a compiler
 * import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
 *
 * import { NgModule } from '@angular/core';
 *
 * // Import the app component
 * import { AppComponent } from './app.component';
 *
 * // Define the app module
 * _@NgModule({
 *     declarations: [AppComponent], // declare app component
 *     imports:      [BrowserModule, RippleModule], // import Ripple module
 *     bootstrap:    [AppComponent]
 * })
 * export class AppModule {}
 *
 * // Compile and launch the module
 * platformBrowserDynamic().bootstrapModule(AppModule);
 *
 * ```
 */
class RippleModule {
}
RippleModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RippleModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RippleModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RippleModule, declarations: [RippleContainerDirective], exports: [RippleContainerDirective] });
RippleModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RippleModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: RippleModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [COMPONENT_DIRECTIVES],
                    exports: [COMPONENT_DIRECTIVES]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { RippleContainerDirective, RippleModule };

